import { ref, watchEffect } from 'vue';

export interface MousePosition {
    x: number;
    y: number;
}

interface UseDragInteractionOptions {
    initialPosition?: MousePosition;
}

export const DRAG_INTERACTIONS = {
    LEFT_MOUSE_BUTTON_CODE: 0,
    ZOOM: {
        MIN: 0.2,
        MAX: 3,
        BUTTON_STEP: 0.15,
    },
    PAN: {
        SENSITIVITY: 0.8, // Multiplier for pan sensitivity (1.0 = full speed, 0.5 = half speed)
    },
} as const;

export const useDragInteraction = (options?: UseDragInteractionOptions) => {
    const INIITAL_POSITION = options?.initialPosition ?? { x: 0, y: 0 };

    const isDragging = ref(false);
    const lastPosition = ref<MousePosition>({ x: 0, y: 0 });
    const position = ref<MousePosition>({ ...INIITAL_POSITION });

    const beginDrag = (e: MouseEvent) => {
        if (e.button !== DRAG_INTERACTIONS.LEFT_MOUSE_BUTTON_CODE) return;

        isDragging.value = true;
        lastPosition.value.x = e.clientX;
        lastPosition.value.y = e.clientY;

        e.preventDefault();
    };

    const handleDrag = (e: MouseEvent) => {
        if (!isDragging.value) return;

        // Calculate the delta from the last position
        const delta = {
            x: e.clientX - lastPosition.value.x,
            y: e.clientY - lastPosition.value.y,
        };

        // Apply sensitivity to make panning slower and more controlled
        position.value.x += delta.x * DRAG_INTERACTIONS.PAN.SENSITIVITY;
        position.value.y += delta.y * DRAG_INTERACTIONS.PAN.SENSITIVITY;

        // Update last position for the next movement calculation
        lastPosition.value.x = e.clientX;
        lastPosition.value.y = e.clientY;
    };

    const endDrag = () => {
        isDragging.value = false;
    };

    watchEffect(() => {
        if (isDragging.value) {
            document.addEventListener('mousemove', handleDrag);
            document.addEventListener('mouseup', endDrag);
        } else {
            document.removeEventListener('mousemove', handleDrag);
            document.removeEventListener('mouseup', endDrag);
        }
    });

    const resetPosition = () => {
        position.value = { ...INIITAL_POSITION };
    };

    return {
        isDragging,
        position,
        beginDrag,
        resetPosition,
    };
};
